import React, { useState, useEffect } from "react";
import {
  Form,
  Select,
  DatePicker,
  Button,
  Card,
  InputNumber,
  Upload,
  Steps,
  message,
  Spin,
  TreeSelect,
  Switch,
  Alert,
  Tag,
} from "antd";
import { LoadingOutlined, InboxOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { getClientsWithoutPaginate } from "../../api/client/clientRequest";
import {
  getSubscriptionTypes,
  getAvailableLocationTypes,
} from "../../api/subscription_types/subscriptionTypesRequest";
import { vehicleWithBlacklistStatus } from "../../api/vehicle/vehicleRequest";
import {
  createSubscription,
  getLocationHierarchy,
} from "../../api/subscriptions/subscriptionsRequest";
import { handleFilename } from "../../utils";
import { getSubscriptionAudiencesByType } from "../../api/subscription_audiences/subscriptionAudiencesRequest";

const { Option } = Select;
const { Dragger } = Upload;
const { Step } = Steps;

const CreateSubscription = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [subscriptionTypes, setSubscriptionTypes] = useState([]);
  const [fileList, setFileList] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [locationTree, setLocationTree] = useState([]);
  const [filteredLocationTree, setFilteredLocationTree] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [formValues, setFormValues] = useState({});
  const [isGlobalAccess, setIsGlobalAccess] = useState(false);
  const [locationTypeRestriction, setLocationTypeRestriction] = useState(null);
  const [maxLocations, setMaxLocations] = useState(null);
  const [accessAll, setAccessAll] = useState(false);
  const [subscriptionAudiences, setSubscriptionAudiences] = useState([]);
  const [loadingAudiences, setLoadingAudiences] = useState(false);
  const [selectedSubscriptionAudienceId, setSelectedSubscriptionAudienceId] =
    useState(null);

  useEffect(() => {
    fetchClients();
    fetchSubscriptionTypes();
    fetchLocationHierarchy();
  }, []);

  const fetchClients = async () => {
    try {
      const data = await getClientsWithoutPaginate();
      setClients(data);
    } catch (error) {
      message.error("Échec de la récupération des clients");
      console.error(error);
    }
  };

  const fetchVehicles = async (clientId) => {
    if (!clientId) return;
    setLoadingVehicles(true);
    try {
      const data = await vehicleWithBlacklistStatus(clientId);
      setVehicles(data || []);
    } catch (error) {
      message.error("Échec de la récupération des véhicules");
      console.error(error);
    } finally {
      setLoadingVehicles(false);
    }
  };

  const fetchSubscriptionTypes = async () => {
    try {
      const data = await getSubscriptionTypes();
      setSubscriptionTypes(data || []);
    } catch (error) {
      message.error("Échec de la récupération des types d'abonnement");
      console.error(error);
    }
  };

  const fetchLocationHierarchy = async () => {
    setLoadingLocations(true);
    try {
      const zones = await getLocationHierarchy();
      const treeData = zones.map((zone) => ({
        title: zone.name,
        value: `zone-${zone.id}`,
        key: `zone-${zone.id}`,
        nodeType: "zone",
        children: zone.sections.map((section) => ({
          title: section.name,
          value: `section-${section.id}`,
          key: `section-${section.id}`,
          nodeType: "section",
          children: section.streets.map((street) => ({
            title: street.name,
            value: `street-${street.id}`,
            key: `street-${street.id}`,
            nodeType: "street",
            children: street.lines.map((line) => ({
              title: line.name,
              value: `line-${line.id}`,
              key: `line-${line.id}`,
              nodeType: "line",
              isLeaf: true,
            })),
          })),
        })),
      }));

      setLocationTree(treeData);
      setFilteredLocationTree(treeData);
    } catch (error) {
      message.error("Échec de la récupération des zones");
      console.error(error);
    } finally {
      setLoadingLocations(false);
    }
  };

  const fetchAvailableLocationTypes = async (subscriptionTypeId) => {
    if (!subscriptionTypeId) return;

    try {
      const data = await getAvailableLocationTypes(subscriptionTypeId);

      setLocationTypeRestriction(data.location_type);
      setMaxLocations(data.max_locations);
      setAccessAll(data.access_all);

      if (data.access_all) {
        setFilteredLocationTree(locationTree);
        setIsGlobalAccess(false);
        return;
      }

      if (data.location_type === "global") {
        setIsGlobalAccess(true);
        setSelectedLocations([{ type: "global", id: 1 }]);
        form.setFieldsValue({ locations: [{ type: "global", id: 1 }] });
        form.setFieldsValue({ global_access: true });
      } else {
        setIsGlobalAccess(false);
        setSelectedLocations([]);
        form.setFieldsValue({ locations: [] });
        filterLocationTree(data.location_type);
        console.log(data.location_type);
      }
    } catch (error) {
      message.error(
        "Échec de la récupération des types d'emplacement disponibles"
      );
      console.error(error);
    }
  };

  const fetchSubscriptionAudiencesByType = async (subscriptionTypeId) => {
    setLoadingAudiences(true);
    try {
      const data = await getSubscriptionAudiencesByType(subscriptionTypeId);
      setSubscriptionAudiences(data);
    } catch (error) {
      message.error("Échec de la récupération des audiences d'abonnement");
    } finally {
      setLoadingAudiences(false);
    }
  };

  const filterLocationTree = (locationType) => {
    if (!locationType || locationType === "global") {
      setFilteredLocationTree(locationTree);
      return;
    }

    if (locationType === "zone") {
      setFilteredLocationTree(
        locationTree.map((zone) => ({
          ...zone,
          children: [],
        }))
      );
    } else if (locationType === "section") {
      const filtered = locationTree.map((zone) => ({
        ...zone,
        checkable: false,
        children: zone.children.map((section) => ({
          ...section,
          children: [],
        })),
      }));
      setFilteredLocationTree(filtered);
    } else if (locationType === "street") {
      const filtered = locationTree.map((zone) => ({
        ...zone,
        checkable: false,
        children: zone.children.map((section) => ({
          ...section,
          checkable: false,
          children: section.children.map((street) => ({
            ...street,
            children: [],
          })),
        })),
      }));
      setFilteredLocationTree(filtered);
    } else if (locationType === "line") {
      const filtered = locationTree.map((zone) => ({
        ...zone,
        checkable: false,
        children: zone.children.map((section) => ({
          ...section,
          checkable: false,
          children: section.children.map((street) => ({
            ...street,
            checkable: false,
            children: street.children.map((street) => ({
              ...street,
              children: street.children,
            })),
          })),
        })),
      }));
      setFilteredLocationTree(filtered);
    }
  };

  const handleClientChange = (clientId) => {
    setVehicles([]);
    fetchVehicles(clientId);
    form.setFieldsValue({ vehicle_id: undefined });
  };

  const handleLocationSelect = (selectedKeys) => {
    if (maxLocations !== null && selectedKeys.length > maxLocations) {
      message.warning(
        `Vous ne pouvez sélectionner que ${maxLocations} emplacement(s) maximum`
      );
      return;
    }

    const processedLocations = selectedKeys.map((key) => {
      const [type, id] = key.split("-");
      return { type, id: parseInt(id) };
    });

    setSelectedLocations(processedLocations);
    form.setFieldsValue({ locations: processedLocations });
  };

  const handleGlobalAccessChange = (checked) => {
    setIsGlobalAccess(checked);
    if (checked) {
      setSelectedLocations([{ type: "global", id: 1 }]);
      form.setFieldsValue({ locations: [{ type: "global", id: 1 }] });
    } else {
      setSelectedLocations([]);
      form.setFieldsValue({ locations: [] });
    }
  };

  const handleSubscriptionTypeChange = (subscriptionTypeId) => {
    fetchAvailableLocationTypes(subscriptionTypeId);
    fetchSubscriptionAudiencesByType(subscriptionTypeId);
    setSelectedSubscriptionAudienceId(null);
    form.setFieldsValue({ subscription_audience_id: undefined }); // Reset form value as well
  };

  const uploadProps = {
    name: "documents",
    multiple: true,
    fileList,
    beforeUpload: (file) => {
      const isValidType = [
        "application/pdf",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "image/jpeg",
        "image/png",
        "image/gif",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "text/plain",
      ].includes(file.type);

      const isLessThan10MB = file.size / 1024 / 1024 < 10;

      if (!isValidType) {
        message.error(`${file.name} n'est pas un type de fichier supporté`);
        return Upload.LIST_IGNORE;
      }

      if (!isLessThan10MB) {
        message.error(`${file.name} dépasse 10MB`);
        return Upload.LIST_IGNORE;
      }

      const processedFile = handleFilename(file);

      if (file.name !== processedFile.name) {
        message.info(
          `Fichier "${file.name}" renommé en "${processedFile.name}"`
        );
      }

      setFileList((prev) => [...prev, processedFile]);
      return false;
    },
    onRemove: (file) => {
      const index = fileList.indexOf(file);
      const newFileList = fileList.slice();
      newFileList.splice(index, 1);
      setFileList(newFileList);
    },
  };

  // Helper function to get display text for client options
  const getClientDisplayInfo = (client) => {
    if (!client) return "";
    switch (client.type) {
      case "individuals":
        return `${client.first_name || ""} ${client.last_name || ""} (${
          client.cin || "N/A"
        })`.trim();
      case "companies":
        return `${client.name || "N/A"} (${client.ice || "N/A"})`;
      case "institutions":
        return `${client.name || "N/A"} (${
          client.public_institution?.name || "Institution"
        })`;
      default:
        return client.name || "Client inconnu";
    }
  };

  const steps = [
    {
      title: "Client & Véhicule",
      content: (
        <div className="space-y-6">
          <Form.Item
            name="client_id"
            label="Client"
            rules={[
              { required: true, message: "Veuillez sélectionner un client" },
            ]}
          >
            <Select
              showSearch
              placeholder="Sélectionner un client (Nom, CIN, ICE...)" // Updated placeholder
              onChange={handleClientChange}
              optionFilterProp="children" // Keep filtering based on the rendered content
              filterOption={(input, option) =>
                // Search within the generated display string
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              loading={clients.length === 0} // Show loading if clients aren't loaded yet
            >
              {clients.map((client) => (
                <Option key={client.id} value={client.id}>
                  {/* Use the helper function for display */}
                  {getClientDisplayInfo(client)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="vehicle_id"
            label="Véhicule"
            rules={[
              { required: true, message: "Veuillez sélectionner un véhicule" },
            ]}
          >
            <Select
              showSearch
              placeholder="Sélectionner un véhicule (Matricule, Modèle...)" // Updated placeholder
              optionFilterProp="children"
              loading={loadingVehicles}
              filterOption={(input, option) =>
                // Search within the vehicle display string
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              disabled={
                !form.getFieldValue("client_id") ||
                (vehicles.length === 0 && !loadingVehicles)
              } // Disable if no client selected or no vehicles
              notFoundContent={
                loadingVehicles ? (
                  <div className="flex justify-center p-2">
                    <Spin size="small" />
                  </div>
                ) : !form.getFieldValue("client_id") ? (
                  "Veuillez d'abord sélectionner un client"
                ) : (
                  "Aucun véhicule disponible pour ce client" // More specific message
                )
              }
            >
              {vehicles.map((vehicle) => (
                <Option 
                  key={vehicle.id} 
                  value={vehicle.id}
                  disabled={vehicle.is_blacklisted}
                  className={vehicle.is_blacklisted ? "text-red-500" : ""}
                >
                  <div className="flex items-center justify-between">
                    <span>
                      {`${vehicle.matricule || "N/A"} - ${
                        vehicle.brand || "N/A"
                      }  ${vehicle.model || ""}`}
                    </span>
                    {vehicle.is_blacklisted && (
                      <Tag color="red" size="small">
                        Liste Noire
                      </Tag>
                    )}
                  </div>
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          {/* Warning message for blacklisted vehicles */}
          {vehicles.some(vehicle => vehicle.is_blacklisted) && (
            <Alert
              message="Attention"
              description="Certains véhicules de ce client sont sur la liste noire et ne peuvent pas être sélectionnés pour créer un abonnement. Ces véhicules sont marqués en rouge et désactivés."
              type="warning"
              showIcon
              style={{ marginTop: 16, width: "fit-content" }}
            />
          )}
        </div>
      ),
    },
    {
      title: "Détails de l'abonnement",
      content: (
        <div className="space-y-6">
          <Form.Item
            name="subscription_type_id"
            label="Type d'abonnement"
            rules={[
              {
                required: true,
                message: "Veuillez sélectionner un type d'abonnement",
              },
            ]}
          >
            <Select
              placeholder="Sélectionner un type d'abonnement"
              onChange={handleSubscriptionTypeChange}
            >
              {subscriptionTypes.map((type) => (
                <Option key={type.id} value={type.id}>
                  {type.name} - {type.description}
                </Option>
              ))}
            </Select>
          </Form.Item>

          {/* Subscription Audience Select */}
          <Form.Item
            name="subscription_audience_id"
            label="Audience d'abonnement"
          >
            <Select
              placeholder="Sélectionner une audience d'abonnement"
              loading={loadingAudiences}
              value={selectedSubscriptionAudienceId}
              onChange={setSelectedSubscriptionAudienceId}
              allowClear
              disabled={subscriptionAudiences.length === 0}
            >
              {subscriptionAudiences.map((aud) => (
                <Option key={aud.id} value={aud.id}>
                  {aud.name} - {aud.description}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            name="start_date"
            label="Date de début"
            rules={[
              {
                required: true,
                message: "Veuillez sélectionner une date de début",
              },
            ]}
          >
            <DatePicker
              className="w-full"
              disabledDate={(current) =>
                current && current < dayjs().subtract(7, "day").startOf("day")
              }
              placeholder="Sélectionner une date"
            />
          </Form.Item>

          <Form.Item
            name="initial_months"
            label="Mois initiaux"
            rules={[
              { required: true, message: "Veuillez entrer le nombre de mois" },
              {
                type: "number",
                min: 1,
                max: 12,
                message: "Doit être entre 1 et 12 mois",
              },
            ]}
          >
            <InputNumber min={1} max={12} className="w-full" disabled />
          </Form.Item>

          <Form.Item
            name="no_registration_fee"
            valuePropName="checked"
            label="Sans frais d'inscription"
          >
            <Switch checkedChildren="Oui" unCheckedChildren="Non" />
          </Form.Item>
        </div>
      ),
    },
    {
      title: "Emplacements",
      content: (
        <div className="space-y-6">
          {(accessAll || locationTypeRestriction === "global") && (
            <Form.Item
              name="global_access"
              valuePropName="checked"
              label="Accès global"
            >
              <Switch
                onChange={handleGlobalAccessChange}
                checkedChildren="Accès global activé"
                unCheckedChildren="Accès spécifique"
                disabled={locationTypeRestriction === "global"}
              />
            </Form.Item>
          )}

          {!isGlobalAccess && (
            <Form.Item
              name="locations"
              label="Emplacements de stationnement"
              rules={[
                {
                  required: !isGlobalAccess,
                  message: "Veuillez sélectionner au moins un emplacement",
                },
              ]}
            >
              <div className="bg-gray-50 p-4 rounded-md">
                {loadingLocations ? (
                  <div className="flex justify-center p-6">
                    <Spin
                      indicator={
                        <LoadingOutlined style={{ fontSize: 24 }} spin />
                      }
                    />
                  </div>
                ) : (
                  <TreeSelect
                    treeData={filteredLocationTree}
                    treeCheckable
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    placeholder="Veuillez sélectionner des emplacements"
                    className="w-full"
                    onChange={handleLocationSelect}
                    treeDefaultExpandAll
                    value={selectedLocations.map(
                      (loc) => `${loc.type}-${loc.id}`
                    )}
                    disabled={
                      isGlobalAccess || locationTypeRestriction === "global"
                    }
                    filterTreeNode={(inputValue, treeNode) => {
                      return (
                        treeNode.title
                          .toLowerCase()
                          .indexOf(inputValue.toLowerCase()) >= 0
                      );
                    }}
                    showSearch
                  />
                )}
              </div>
            </Form.Item>
          )}

          {isGlobalAccess && (
            <div className="bg-green-50 p-4 rounded-md text-sm text-green-700 border border-green-200">
              <p>
                <strong>Accès global activé :</strong> Ce véhicule sera autorisé
                à stationner n'importe où, sans restriction d'emplacement.
              </p>
            </div>
          )}

          {!isGlobalAccess && maxLocations && (
            <div className="bg-blue-50 p-4 rounded-md text-sm text-blue-700 border border-blue-200">
              <p>
                <strong>Restriction :</strong> Ce type d'abonnement vous permet
                de sélectionner un maximum de {maxLocations} emplacement(s) de
                type{" "}
                {locationTypeRestriction === "zone"
                  ? "zone"
                  : locationTypeRestriction === "section"
                  ? "section"
                  : locationTypeRestriction === "street"
                  ? "rue"
                  : locationTypeRestriction === "line"
                  ? "ligne"
                  : "quelconque"}
                .
              </p>
            </div>
          )}

          {!isGlobalAccess && !locationTypeRestriction && (
            <div className="bg-blue-50 p-4 rounded-md text-sm text-blue-700 border border-blue-200">
              <p>
                <strong>Remarque :</strong> La sélection d'une zone permettra le
                stationnement dans toutes les sections, rues et lignes de cette
                zone.
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Documents",
      content: (
        <div className="space-y-6">
          <Form.Item name="documents" label="Télécharger des documents">
            <Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">
                Cliquez ou faites glisser un fichier dans cette zone pour le
                télécharger
              </p>
              <p className="ant-upload-hint">
                Support pour PDF, Word, Excel, images (JPG, PNG, GIF) et TXT.
                Maximum 10MB par fichier.
              </p>
            </Dragger>
          </Form.Item>
        </div>
      ),
    },
  ];

  const next = async () => {
    try {
      await form.validateFields(steps[currentStep].fields);
      const currentValues = form.getFieldsValue();
      setFormValues({ ...formValues, ...currentValues });
      setCurrentStep(currentStep + 1);
    } catch (error) {
      console.error("Échec de la validation:", error);
    }
  };

  const prev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      const values = { ...form.getFieldsValue(), ...formValues };

      setLoading(true);

      const formData = new FormData();

      Object.keys(values).forEach((key) => {
        if (key === "start_date") {
          formData.append(key, values[key].format("YYYY-MM-DD"));
        } else if (key === "locations" && !isGlobalAccess) {
          values.locations.forEach((location, index) => {
            formData.append(`locations[${index}][type]`, location.type);
            formData.append(`locations[${index}][id]`, location.id);
          });
        } else if (key !== "documents" && key !== "global_access") {
          formData.append(key, values[key]);
        } else if (isGlobalAccess) {
          formData.append("locations[0][type]", "global");
          formData.append("locations[0][id]", 1);
        }
      });

      // Add subscription_audience_id if present
      if (!values.subscription_audience_id) {
        formData.delete(
          "subscription_audience_id",
          values.subscription_audience_id
        );
      }

      fileList.forEach((file, index) => {
        formData.append(`documents[${index}]`, file);
      });

      await createSubscription(formData);

      message.success("Abonnement créé avec succès");
      navigate("/subscriptions");
    } catch (error) {
      console.error("Erreur lors de la création de l'abonnement:", error);
      message.error(
        "Échec de la création de l'abonnement: " +
          (error.response?.data?.error ||
            error.response?.data?.message ||
            "Erreur inconnue")
      );
    } finally {
      setLoading(false);
    }
  };

  // Fix useEffect dependency warning
  useEffect(() => {
    const locations = form.getFieldValue("locations");
    if (locations) {
      setSelectedLocations(locations);
    }
  }, [form]);

  steps[0].fields = ["client_id", "vehicle_id"];
  steps[1].fields = [
    "subscription_type_id",
    "subscription_audience_id",
    "start_date",
    "initial_months",
  ];
  steps[2].fields = ["locations"];
  steps[3].fields = [];

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold text-gray-900">
          Créer un nouvel abonnement
        </h1>
        <p className="mt-2 text-sm text-gray-700">
          Créez un nouvel abonnement de stationnement pour le véhicule d'un
          client.
        </p>
      </div>

      <Card>
        <Steps current={currentStep} className="mb-8">
          {steps.map((item) => (
            <Step key={item.title} title={item.title} />
          ))}
        </Steps>

        <Form
          form={form}
          layout="vertical"
          initialValues={{ initial_months: 1, no_registration_fee: false }}
        >
          <div className="p-2">{steps[currentStep].content}</div>

          <div className="flex justify-between mt-8">
            {currentStep > 0 && (
              <Button onClick={prev} disabled={loading} className="mr-2">
                Précédent
              </Button>
            )}

            {currentStep < steps.length - 1 && (
              <Button type="primary" onClick={next}>
                Suivant
              </Button>
            )}

            {currentStep === steps.length - 1 && (
              <Button type="primary" onClick={handleSubmit} loading={loading}>
                Soumettre
              </Button>
            )}
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default CreateSubscription;
